import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Heart, MessageCircle, Send, Plus, Image, Video, X, Play, Search, Share2, UserPlus, Sparkles } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import VideoEditor from './VideoEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShortsAndPosts = () => {
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/posts/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = () => {
    setShowCreateDialog(false);
    fetchPosts();
  };

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      post.username?.toLowerCase().includes(query) ||
      post.caption?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="shorts-feed">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Shorts</h2>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="create-post-btn">
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <CreatePostDialog onClose={handlePostCreated} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search shorts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-9"
          data-testid="search-posts-input"
        />
      </div>

      {filteredPosts.length === 0 ? (
        <div className="text-center py-16">
          <Video className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-base font-medium text-gray-600">
            {searchQuery ? 'No shorts found' : 'No shorts yet'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {searchQuery ? 'Try a different search' : 'Share your best rallies and highlights'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowCreateDialog(true)} variant="outline" size="sm" className="mt-4">
              Post your first short
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <PostCard key={post.id} post={post} onUpdate={fetchPosts} />
          ))}
        </div>
      )}
    </div>
  );
};

// Create Post Dialog with tagging
const CreatePostDialog = ({ onClose }) => {
  const { token } = useAuth();
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [tagResults, setTagResults] = useState([]);
  const [showTagSearch, setShowTagSearch] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [rawVideoFile, setRawVideoFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 50MB', variant: 'destructive' });
      return;
    }

    // Enforce short-form video (max 60 seconds)
    if (file.type.startsWith('video/')) {
      const duration = await new Promise((resolve) => {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => { resolve(vid.duration); URL.revokeObjectURL(vid.src); };
        vid.onerror = () => resolve(0);
        vid.src = URL.createObjectURL(file);
      });
      if (duration > 120) {
        toast({ title: 'Video too long', description: 'Shorts must be 2 minutes or less.', variant: 'destructive' });
        return;
      }
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/posts/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setMediaUrl(response.data.media_url);
      setMediaType(response.data.media_type);
      if (response.data.media_type === 'video') {
        setRawVideoFile(URL.createObjectURL(file));
      }
      toast({ title: 'Media uploaded!' });
    } catch (error) {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const searchFriends = async (query) => {
    setTagSearch(query);
    if (query.length < 2) { setTagResults([]); return; }
    try {
      const response = await axios.get(`${API}/friends/search/${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTagResults(response.data.filter(u => !taggedUsers.find(t => t.id === u.id)));
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const addTag = (user) => {
    setTaggedUsers([...taggedUsers, { id: user.id, username: user.username }]);
    setTagSearch('');
    setTagResults([]);
  };

  const removeTag = (userId) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };

  const handlePost = async () => {
    if (!mediaUrl) {
      toast({ title: 'No media', description: 'Upload an image or video first', variant: 'destructive' });
      return;
    }
    setPosting(true);
    try {
      await axios.post(`${API}/posts/`, {
        media_type: mediaType,
        media_url: mediaUrl,
        caption,
        tagged_users: taggedUsers.map(u => u.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Posted!' });
      onClose();
    } catch (error) {
      toast({ title: 'Failed to post', variant: 'destructive' });
    } finally {
      setPosting(false);
    }
  };

  const handleVideoEdited = async (blob) => {
    setUploading(true);
    setShowVideoEditor(false);
    try {
      const formData = new FormData();
      formData.append('file', new File([blob], 'edited-video.webm', { type: 'video/webm' }));
      const response = await axios.post(`${API}/posts/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setMediaUrl(response.data.media_url);
      setMediaType('video');
      toast({ title: 'Video edited and saved!' });
    } catch (error) {
      toast({ title: 'Failed to save edited video', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  if (showVideoEditor && rawVideoFile) {
    return (
      <div className="max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit Video</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <VideoEditor
            videoUrl={rawVideoFile}
            onSave={handleVideoEdited}
            onCancel={() => setShowVideoEditor(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create New Post</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        {mediaUrl ? (
          <div className="relative rounded-lg overflow-hidden bg-black">
            {mediaType === 'video' ? (
              <video src={mediaUrl} controls className="w-full max-h-64 object-contain" />
            ) : (
              <img src={mediaUrl} alt="Preview" className="w-full max-h-64 object-contain" />
            )}
            <div className="absolute top-2 right-2 flex gap-1">
              {mediaType === 'video' && (
                <Button
                  variant="ghost" size="sm"
                  className="bg-blue-500/80 text-white hover:bg-blue-600/80"
                  onClick={() => setShowVideoEditor(true)}
                  data-testid="edit-video-btn"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
              <Button
                variant="ghost" size="sm"
                className="bg-black/50 text-white hover:bg-black/70"
                onClick={() => { setMediaUrl(''); setMediaType(''); setRawVideoFile(null); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            ) : (
              <>
                <div className="flex justify-center gap-4 mb-3">
                  <Image className="h-10 w-10 text-gray-400" />
                  <Video className="h-10 w-10 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Click to upload</p>
                <p className="text-sm text-gray-400">Images or Videos (max 50MB)</p>
              </>
            )}
          </div>
        )}

        <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />

        <Textarea
          placeholder="Write a caption..."
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={3}
          data-testid="post-caption-input"
        />

        {/* Tag People */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Tag People</span>
          </div>
          {taggedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {taggedUsers.map(u => (
                <Badge key={u.id} variant="secondary" className="flex items-center gap-1">
                  {u.username}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(u.id)} />
                </Badge>
              ))}
            </div>
          )}
          <div className="relative">
            <Input
              placeholder="Search friends to tag..."
              value={tagSearch}
              onChange={(e) => searchFriends(e.target.value)}
              onFocus={() => setShowTagSearch(true)}
              data-testid="tag-search-input"
            />
            {showTagSearch && tagResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {tagResults.map(u => (
                  <button
                    key={u.id}
                    onClick={() => addTag(u)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                    data-testid={`tag-option-${u.id}`}
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={u.profile_image} />
                      <AvatarFallback>{u.username[0]}</AvatarFallback>
                    </Avatar>
                    {u.username}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handlePost}
          disabled={!mediaUrl || posting}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="submit-post-btn"
        >
          {posting ? 'Posting...' : 'Post'}
        </Button>
      </DialogFooter>
    </>
  );
};

// Post Card with share button
const PostCard = ({ post, onUpdate }) => {
  const { user, token } = useAuth();
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [friends, setFriends] = useState([]);
  const videoRef = useRef(null);

  const handleLike = async () => {
    try {
      const response = await axios.post(`${API}/posts/${post.id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLiked(response.data.action === 'liked');
      setLikesCount(response.data.likes_count);
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/posts/${post.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    try {
      const response = await axios.post(`${API}/posts/${post.id}/comments`, {
        content: newComment
      }, { headers: { Authorization: `Bearer ${token}` } });
      setComments([...comments, response.data]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const toggleComments = () => {
    if (!showComments) fetchComments();
    setShowComments(!showComments);
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) { videoRef.current.pause(); } else { videoRef.current.play(); }
      setIsPlaying(!isPlaying);
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    }
  };

  const handleShare = async (friendId) => {
    try {
      await axios.post(`${API}/posts/${post.id}/share/${friendId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Post shared!' });
      setShowShareDialog(false);
    } catch (error) {
      toast({ title: 'Failed to share', variant: 'destructive' });
    }
  };

  const openShareDialog = () => {
    fetchFriends();
    setShowShareDialog(true);
  };

  return (
    <Card className="overflow-hidden" data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={post.user_profile_image} />
            <AvatarFallback>{post.username?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold">{post.username}</p>
            <p className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        {post.is_match_highlight && (
          <Badge className="bg-yellow-500">Match Highlight</Badge>
        )}
      </div>

      {/* Media */}
      <div className="relative bg-black">
        {post.media_type === 'video' ? (
          <div className="relative" onClick={toggleVideo}>
            <video ref={videoRef} src={post.media_url} className="w-full max-h-[500px] object-contain cursor-pointer" loop playsInline />
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-black/50 rounded-full p-4">
                  <Play className="h-8 w-8 text-white" fill="white" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <img src={post.media_url} alt="Post" className="w-full max-h-[500px] object-contain" />
        )}
      </div>

      {/* Actions */}
      <CardContent className="p-4">
        <div className="flex items-center gap-4 mb-3">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : 'text-gray-600'}`}
            data-testid={`like-btn-${post.id}`}
          >
            <Heart className={`h-6 w-6 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-sm font-medium">{likesCount}</span>
          </button>
          <button onClick={toggleComments} className="flex items-center gap-1 text-gray-600" data-testid={`comment-btn-${post.id}`}>
            <MessageCircle className="h-6 w-6" />
          </button>
          <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
            <DialogTrigger asChild>
              <button onClick={openShareDialog} className="flex items-center gap-1 text-gray-600" data-testid={`share-btn-${post.id}`}>
                <Share2 className="h-6 w-6" />
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Share with Friend</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-4 max-h-64 overflow-y-auto">
                {friends.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No friends to share with</p>
                ) : (
                  friends.map(friend => (
                    <button
                      key={friend.id}
                      onClick={() => handleShare(friend.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid={`share-to-${friend.id}`}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={friend.profile_image} />
                        <AvatarFallback>{friend.username[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-sm">{friend.username}</span>
                      <Send className="h-4 w-4 ml-auto text-blue-500" />
                    </button>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tagged Users */}
        {post.tagged_users?.length > 0 && (
          <div className="flex items-center gap-1 mb-2 flex-wrap">
            <UserPlus className="h-3 w-3 text-gray-400" />
            {post.tagged_users.map((tu, i) => (
              <span key={tu.id || i} className="text-xs text-blue-600 font-medium">
                @{tu.username}{i < post.tagged_users.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}

        {/* Caption */}
        {post.caption && (
          <p className="text-sm">
            <span className="font-semibold">{post.username}</span>{' '}
            {post.caption}
          </p>
        )}

        {/* Comments */}
        {showComments && (
          <div className="mt-4 space-y-3">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={comment.user_profile_image} />
                  <AvatarFallback>{comment.username[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm">
                    <span className="font-semibold">{comment.username}</span>{' '}
                    {comment.content}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                className="flex-1"
              />
              <Button size="sm" onClick={handleComment}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ShortsAndPosts;
